import { useState } from "react";
import { Check, Sparkles, Zap, Rocket, Layout, Stethoscope, UtensilsCrossed, Briefcase, Home, ArrowRight, RotateCw, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useBookCall } from "@/pages/Landing";
import { api } from "@/lib/api";

const PACKAGES = [
  {
    id: "starter",
    deposit_id: "starter",
    deposit_amount: 500,
    name: "Starter Website",
    price: "Starting at $1,499",
    tagline: "A clean, capable web presence — built right the first time.",
    icon: Zap,
    accent: "cyan",
    tag: "// PKG_01",
    target: ["Landscapers", "Pressure washing", "Cleaning cos.", "Mobile detailers", "Handymen"],
    includes: [
      { t: "Five thoughtfully built pages", d: "Home, About, Services, Reviews, and Contact — each one written with intention." },
      { t: "Fully mobile responsive", d: "Looks polished on every device your customers actually use." },
      { t: "Contact form + Google Maps", d: "Inquiries arrive in your inbox. Directions to your door are one tap away." },
      { t: "SEO fundamentals", d: "Configured properly so search engines can find and trust your site." },
      { t: "AI-assisted copywriting", d: "Modern tools used to write content in your voice — refined by a human eye." },
    ],
  },
  {
    id: "leadgen",
    deposit_id: "leadgen",
    deposit_amount: 1000,
    name: "Lead Generation Website",
    price: "$1,997",
    tagline: "A web presence designed to bring you qualified work — month after month.",
    icon: Rocket,
    accent: "orange",
    tag: "// PKG_02",
    featured: true,
    target: ["Roofers", "HVAC", "Electricians", "Plumbers", "Remodelers"],
    includes: [
      { t: "10–15 page architecture", d: "A dedicated page for each service so search engines can rank you for every offer." },
      { t: "Lead capture funnels", d: "Forms and pathways designed to turn casual visitors into booked appointments." },
      { t: "CRM integration", d: "Every lead organized in one place — easy to track, easy to close." },
      { t: "Automated email follow-up", d: "Timely, professional follow-up while you focus on the work in front of you." },
      { t: "Local SEO foundation", d: "Optimized to perform in the cities and zip codes that matter to your business." },
    ],
  },
  {
    id: "system",
    deposit_id: "system",
    deposit_amount: 1500,
    name: "Complete Marketing System",
    price: "$3,500–$5,000",
    tagline: "Everything you need to run a serious local business — handled.",
    icon: Sparkles,
    accent: "lime",
    tag: "// PKG_03",
    target: ["Established local brands", "Multi-location service cos.", "Growing practices"],
    includes: [
      { t: "Premium website build", d: "Considered design, refined copy, and the technical polish to match." },
      { t: "AI assistant on your site", d: "Answers common customer questions 24/7 — like a quiet, capable receptionist." },
      { t: "Online appointment booking", d: "Clients schedule themselves. No more phone tag, no missed opportunities." },
      { t: "CRM + email automation", d: "Every lead nurtured and followed up with professionalism, on time." },
      { t: "Review generation system", d: "A graceful way to ask satisfied clients for the reviews that grow your reputation." },
      { t: "Lead nurturing sequences", d: "The right message reaches the right person at the right moment." },
    ],
  },
];

const TEMPLATES = [
  { letter: "A", name: "Home Service Businesses", icon: Home, accent: "cyan" },
  { letter: "B", name: "Medical & Wellness", icon: Stethoscope, accent: "lime" },
  { letter: "C", name: "Restaurants", icon: UtensilsCrossed, accent: "orange" },
  { letter: "D", name: "Professional Services", icon: Briefcase, accent: "pink" },
  { letter: "E", name: "Real Estate", icon: Layout, accent: "cyan" },
];

const RETAINERS = [
  { name: "SEO", price: "$300–$800/mo", blurb: "Steady, methodical work to earn your spot at the top of search results." },
  { name: "Google Business Profile", price: "$200–$500/mo", blurb: "Your Google listing kept current, accurate, and optimized for local visibility." },
  { name: "Reputation Management", price: "$150–$400/mo", blurb: "Growing your five-star reviews and handling the difficult ones with care." },
  { name: "AI Assistant Management", price: "$100–$300/mo", blurb: "Your on-site assistant trained and refined each month to match how your business evolves." },
  { name: "Lead Follow-up Automation", price: "$200–$500/mo", blurb: "Every inquiry receives a timely, professional response — automatically." },
];

const ACCENT = {
  cyan: { border: "border-neon-cyan/40", text: "text-neon-cyan", bg: "bg-neon-cyan/5", glow: "shadow-[0_0_30px_rgba(0,255,255,0.15)]", btn: "!bg-neon-cyan !border-neon-cyan !text-[#05050A] hover:!bg-transparent hover:!text-neon-cyan" },
  orange: { border: "border-neon-orange/50", text: "text-neon-orange", bg: "bg-neon-orange/5", glow: "shadow-[0_0_40px_rgba(255,94,0,0.25)]", btn: "!bg-neon-orange !border-neon-orange hover:!bg-transparent hover:!text-neon-orange" },
  lime: { border: "border-neon-lime/40", text: "text-neon-lime", bg: "bg-neon-lime/5", glow: "shadow-[0_0_30px_rgba(57,255,20,0.15)]", btn: "!bg-neon-lime !border-neon-lime !text-[#05050A] hover:!bg-transparent hover:!text-neon-lime" },
  pink: { border: "border-neon-pink/40", text: "text-neon-pink", bg: "bg-neon-pink/5", glow: "shadow-[0_0_30px_rgba(255,0,127,0.15)]", btn: "" },
};

export default function Packages() {
  const book = useBookCall();
  const [paying, setPaying] = useState(null);

  const reserveSlot = async (pkg) => {
    setPaying(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.deposit_id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again or book a call.");
      setPaying(null);
    }
  };

  return (
    <section id="packages" data-testid="packages-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline mb-4">// PRODUCTIZED_OFFERS</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Custom-designed websites,<br />
              <span className="text-neon-orange neon-text-orange">built properly.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">
            Three transparent packages — each one designed and built specifically for your business, your audience, and your brand. <span className="text-white">No templated work. No generic copy.</span> Crafted with the precision and skill your business deserves.
          </p>
        </div>

        {/* Pricing cards */}
        <div data-testid="package-grid" className="grid lg:grid-cols-3 gap-5 md:gap-6 mb-24">
          {PACKAGES.map((p) => {
            const a = ACCENT[p.accent];
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                data-testid={`package-${p.id}`}
                className={`relative glass p-8 md:p-9 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  p.featured ? `${a.border} border-2 ${a.glow}` : "glass-hover"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-7 px-3 py-1 bg-neon-orange text-[#05050A] font-mono text-[10px] uppercase tracking-widest font-bold">
                    Most Popular
                  </div>
                )}

                <div className={`w-12 h-12 flex items-center justify-center border ${a.border} ${a.bg} mb-6`}>
                  <Icon size={20} strokeWidth={1.5} className={a.text} />
                </div>

                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                  {p.tag}
                </div>
                <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-2">{p.name}</h3>
                <p className="text-white/60 text-sm mb-5 leading-snug">{p.tagline}</p>
                <div className={`font-heading font-black text-3xl md:text-4xl ${a.text} mb-1 leading-none`}>{p.price}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 mb-6">
                  one-time investment
                </div>

                <div className="overline mb-3">// What you get</div>
                <ul className="space-y-3 mb-6 flex-1">
                  {p.includes.map((it) => (
                    <li key={it.t} className="flex items-start gap-2.5 text-sm">
                      <Check size={14} className={`${a.text} flex-shrink-0 mt-0.5`} strokeWidth={2.5} />
                      <div>
                        <div className="text-white font-semibold">{it.t}</div>
                        <div className="text-white/50 text-[12.5px] leading-snug mt-0.5">{it.d}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="overline mb-3">// Best for</div>
                <div className="flex flex-wrap gap-1.5 mb-7">
                  {p.target.map((t) => (
                    <span key={t} className="px-2 py-1 border border-white/10 text-[11px] font-mono uppercase tracking-wider text-white/60">
                      {t}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => book.open(p.name)}
                  data-testid={`package-cta-${p.id}`}
                  className={`btn-primary justify-center w-full ${a.btn}`}
                >
                  Start a Conversation <ArrowRight size={14} />
                </button>
                <button
                  onClick={() => reserveSlot(p)}
                  disabled={paying === p.id}
                  data-testid={`package-deposit-${p.id}`}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/10 font-mono text-[11px] uppercase tracking-widest text-white/60 hover:text-white hover:border-white/40 transition-all disabled:opacity-50"
                >
                  {paying === p.id ? (
                    "Redirecting…"
                  ) : (
                    <>
                      <CreditCard size={11} /> Reserve slot · ${p.deposit_amount} deposit
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Templates */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <div className="overline mb-4 flex items-center gap-2 text-neon-cyan">
                <RotateCw size={14} /> // INDUSTRY_FOUNDATIONS
              </div>
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                Refined over time.<br />
                <span className="text-white/40">Tailored to you.</span>
              </h2>
            </div>
            <p className="lg:col-span-7 text-white/60 text-base md:text-lg max-w-2xl">
              Each build begins from one of five industry foundations we&apos;ve developed and refined over many projects. Modern tools let us adapt the copy, imagery, and structure <span className="text-white">specifically to your business</span> — without the long timelines or open-ended invoices typical of custom work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
            {TEMPLATES.map((t) => {
              const a = ACCENT[t.accent];
              const Icon = t.icon;
              return (
                <div
                  key={t.letter}
                  data-testid={`template-${t.letter}`}
                  className="group bg-[#05050A] p-7 hover:bg-white/[0.03] transition-all relative overflow-hidden"
                >
                  <div className={`font-heading font-black text-7xl ${a.text} opacity-30 group-hover:opacity-100 transition-opacity leading-none mb-4`}>
                    {t.letter}
                  </div>
                  <div className={`w-9 h-9 flex items-center justify-center border ${a.border} ${a.bg} mb-3`}>
                    <Icon size={15} className={a.text} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-base leading-tight">{t.name}</h3>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly retainers */}
        <div data-testid="retainers-block" className="glass-strong relative p-10 md:p-14 overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-neon-orange/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="overline mb-4 text-neon-orange">// ONGOING_PARTNERSHIP</div>
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-6">
                Stay supported,<br />
                <span className="text-neon-orange">month to month.</span>
              </h2>
              <p className="text-white/60 text-base md:text-lg max-w-md mb-3">
                A great website is a starting point. These ongoing services keep your visibility growing, your reputation strong, and your inquiries converted — <span className="text-white">while you focus on serving your clients well.</span>
              </p>
              <p className="text-white/40 text-sm mb-8">
                Choose the services that fit. Adjust as you grow. We&apos;ll be straightforward about what you actually need.
              </p>
              <button
                onClick={() => book.open("Marketing Retainer")}
                data-testid="retainer-cta"
                className="btn-primary"
              >
                Discuss a Plan <ArrowRight size={14} />
              </button>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-px bg-white/10">
                {RETAINERS.map((r, i) => (
                  <div
                    key={r.name}
                    data-testid={`retainer-${i}`}
                    className="bg-[#0F0F16] p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="font-mono text-xs text-white/30 mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-heading font-bold text-base md:text-lg">{r.name}</div>
                        <div className="text-white/50 text-sm mt-0.5">{r.blurb}</div>
                      </div>
                    </div>
                    <div className="font-mono text-sm md:text-base text-neon-orange font-semibold whitespace-nowrap sm:ml-4 pl-10 sm:pl-0">
                      {r.price}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
